import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { mediaUrl } from '../utils/mediaUrl';

interface VideoPlayerProps {
  streamUrl: string;
  /** Overlay elements rendered inside the video container (score bug, ticker, etc.) */
  children?: React.ReactNode;
  /** Called whenever the custom fullscreen state changes */
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

// Detect HLS.js support once (false on iOS Safari / WKWebView which natively support HLS)
const HLS_SUPPORTED = Hls.isSupported();

const ZOOM_PRESETS = [1, 1.5, 2, 2.5, 3];

/** True when running inside a Capacitor native shell (iOS / Android). */
function isCapacitorNative(): boolean {
  try {
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) return true;
    if (cap?.isNative === true) return true;
  } catch { /* ignore */ }
  const { protocol, hostname, port } = window.location;
  if (protocol === 'capacitor:' || protocol === 'ionic:') return true;
  if (hostname === 'localhost' && port === '') return true;
  return false;
}

/** Try to show/hide Capacitor StatusBar — silently does nothing outside Capacitor. */
async function setStatusBarVisible(visible: boolean) {
  try {
    const { StatusBar } = await import('@capacitor/status-bar');
    if (visible) {
      await StatusBar.show();
    } else {
      await StatusBar.hide();
    }
  } catch { /* plugin not available or not native — ignore */ }
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ streamUrl: rawStreamUrl, children, onFullscreenChange }) => {
  const streamUrl  = mediaUrl(rawStreamUrl);
  const videoRef   = useRef<HTMLVideoElement>(null);
  const hlsRef     = useRef<Hls | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [error,        setError]        = useState<string | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom,         setZoom]         = useState(1);
  const [reconnecting, setReconnecting] = useState(false);
  // Incrementing this key re-runs the HLS setup effect to reinitialise playback
  const [retryKey,     setRetryKey]     = useState(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── HLS / native playback setup ─────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) return;

    setError(null);
    setLoading(true);
    setReconnecting(false);
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }

    const isHlsStream = streamUrl.toLowerCase().includes('.m3u8');

    // Poll the m3u8 URL until it responds, then increment retryKey to re-init HLS.
    const beginReconnect = (hls?: Hls) => {
      if (hls) { hls.destroy(); hlsRef.current = null; }
      setReconnecting(true);
      setLoading(false);
      setError(null);
      const poll = () => {
        fetch(streamUrl, { method: 'HEAD', cache: 'no-store' })
          .then(r => {
            if (r.ok) {
              setReconnecting(false);
              setRetryKey(k => k + 1);
            } else {
              reconnectTimer.current = setTimeout(poll, 3000);
            }
          })
          .catch(() => { reconnectTimer.current = setTimeout(poll, 3000); });
      };
      reconnectTimer.current = setTimeout(poll, 3000);
    };

    if (isHlsStream && HLS_SUPPORTED) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 30,
        maxBufferLength: 10,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 15,
      });
      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false);
        video.play().catch(() => {});
      });

      // Stream ended (camera dropped) — poll until it comes back, auto-resume
      hls.on(Hls.Events.LEVEL_UPDATED, (_e, data) => {
        if (!data.details.live) beginReconnect(hls);
      });

      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            beginReconnect(hls);
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            setError('Playback error. Please refresh the page.');
            hls.destroy();
            setLoading(false);
          }
        }
      });

    } else if (isHlsStream && video.canPlayType('application/vnd.apple.mpegurl')) {
      // iOS Safari / Capacitor WKWebView — native HLS
      video.src = streamUrl;
      setLoading(false);
      video.play().catch(() => {});

      // Auto-reconnect for native HLS: stream end fires 'ended', drop fires 'error'
      const onEnded = () => beginReconnect();
      const onErr   = () => beginReconnect();
      video.addEventListener('ended', onEnded);
      video.addEventListener('error', onErr);
      return () => {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('error', onErr);
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      };

    } else if (isHlsStream) {
      setError('Your browser does not support HLS video playback.');
      setLoading(false);
    } else {
      // Direct video file (MP4, WebM, etc.)
      video.src = streamUrl;
      const onCanPlay = () => setLoading(false);
      const onError   = () => { setError('Video could not be loaded. Please try again.'); setLoading(false); };
      video.addEventListener('canplay', onCanPlay, { once: true });
      video.addEventListener('error',   onError,   { once: true });
      video.load();
      video.play().catch(() => {});
      return () => {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('error',   onError);
        if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      };
    }

    return () => {
      if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, [streamUrl, retryKey]); // retryKey increments to force re-init after stream recovers

  // Reset zoom when stream changes
  useEffect(() => { setZoom(1); }, [streamUrl]);

  // ── Custom fullscreen lifecycle ──────────────────────────────────────────────

  // Shared logic when we switch to CSS-based fullscreen (Capacitor / fallback)
  const applyCssFullscreen = () => {
    setIsFullscreen(true);
    onFullscreenChange?.(true);
    document.body.style.overflow = 'hidden';
    // Hide tab bar + top nav so they don't bleed over the fullscreen video
    document.body.classList.add('video-fullscreen');
    setStatusBarVisible(false);
  };

  const removeCssFullscreen = () => {
    setIsFullscreen(false);
    onFullscreenChange?.(false);
    document.body.style.overflow = '';
    document.body.classList.remove('video-fullscreen');
    setStatusBarVisible(true);
  };

  const enterFullscreen = () => {
    const el = wrapperRef.current;
    if (!el) { applyCssFullscreen(); return; }

    // ── Try native Fullscreen API first ──
    // Supported on Safari 16.4+ (covers browser toolbar on iPhone),
    // Chrome, Firefox, Edge — but NOT in Capacitor WKWebView.
    const reqFS = el.requestFullscreen ?? (el as any).webkitRequestFullscreen;
    if (reqFS && !isCapacitorNative()) {
      (reqFS.bind(el) as () => Promise<void>)()
        .then(() => {
          // fullscreenchange event will call setIsFullscreen(true)
        })
        .catch(() => {
          // Browser refused (e.g. gesture timing) — fall back to CSS
          applyCssFullscreen();
        });
    } else {
      // Capacitor (no browser chrome to worry about, CSS works perfectly)
      applyCssFullscreen();
    }
  };

  const exitFullscreen = () => {
    const inNativeFS = !!(document.fullscreenElement ?? (document as any).webkitFullscreenElement);
    if (inNativeFS) {
      (document.exitFullscreen?.() ?? (document as any).webkitExitFullscreen?.());
      // fullscreenchange event will call setIsFullscreen(false)
    } else {
      removeCssFullscreen();
    }
  };

  const toggleFullscreen = () => (isFullscreen ? exitFullscreen : enterFullscreen)();

  // ── Native fullscreen events (Safari, Chrome) ────────────────────────────────
  // When requestFullscreen() succeeds, the browser fires fullscreenchange.
  // Sync our state so the overlay buttons render correctly.
  useEffect(() => {
    const onFSChange = () => {
      const inFS = !!(document.fullscreenElement ?? (document as any).webkitFullscreenElement);
      setIsFullscreen(inFS);
      onFullscreenChange?.(inFS);
      if (!inFS) {
        document.body.style.overflow = '';
        document.body.classList.remove('video-fullscreen');
      }
    };
    document.addEventListener('fullscreenchange',       onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('fullscreenchange',       onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
    };
  // onFullscreenChange is stable (passed once from parent) — safe to omit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape key exits both native and CSS fullscreen
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isFullscreen) exitFullscreen(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  // Resume playback after fullscreen toggle — layout shift can pause on iOS WKWebView
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const t = setTimeout(() => {
      if (video.paused && !video.ended && video.readyState >= 2) {
        video.play().catch(() => {});
      }
    }, 80);
    return () => clearTimeout(t);
  }, [isFullscreen]);

  // Release body overflow lock on unmount
  useEffect(() => () => {
    document.body.style.overflow = '';
    document.body.classList.remove('video-fullscreen');
    setStatusBarVisible(true);
  }, []);

  // ── Styles ───────────────────────────────────────────────────────────────────
  //
  // CSS fullscreen (Capacitor / fallback):
  //   position:fixed covers the viewport exactly. On Capacitor the viewport IS
  //   the whole screen, so it's truly edge-to-edge.
  //   paddingTop/paddingBottom use env(safe-area-inset-*) so the video and controls
  //   stay clear of the iPhone notch and home-indicator bar.
  //
  // Native fullscreen (Safari 16.4+ browser):
  //   requestFullscreen() handles the presentation — the element receives the
  //   :fullscreen pseudo-class. Our `isFullscreen` state is still set so the
  //   background and control styles apply inside the fullscreen context.
  //
  const wrapperStyle: React.CSSProperties = isFullscreen
    ? {
        position:       'fixed',
        inset:          0,
        zIndex:         9000,
        background:     '#000',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        overflow:       'hidden',
        // Push content clear of iPhone notch (top) and home indicator (bottom)
        paddingTop:    'env(safe-area-inset-top,    0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }
    : {
        position:     'relative',
        background:   '#000',
        borderRadius: '10px',
        overflow:     'hidden',
        aspectRatio:  '16/9',
      };

  const videoStyle: React.CSSProperties = {
    display:         'block',
    width:           '100%',
    height:          '100%',
    objectFit:       'contain',
    transform:       `scale(${zoom})`,
    transformOrigin: 'center center',
    transition:      'transform 0.2s ease',
  };

  // Shared glass style used by both zoom buttons and fullscreen button
  const glassSurface: React.CSSProperties = {
    background:           'rgba(0,0,0,0.52)',
    backdropFilter:       'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    border:               '1px solid rgba(255,255,255,0.18)',
  };

  const fsBtnStyle: React.CSSProperties = {
    ...glassSurface,
    position:         'absolute',
    bottom:           8,
    right:            8,
    zIndex:           30,
    width:            34,
    height:           34,
    borderRadius:     7,
    color:            '#fff',
    cursor:           'pointer',
    display:          'flex',
    alignItems:       'center',
    justifyContent:   'center',
    padding:          0,
    touchAction:      'manipulation',
    userSelect:       'none',
    WebkitUserSelect: 'none' as any,
  };

  return (
    <div ref={wrapperRef} style={wrapperStyle} className="video-player-wrapper">
      <video
        ref={videoRef}
        playsInline
        controls
        controlsList="nofullscreen"
        x-webkit-airplay="allow"
        style={videoStyle}
      />

      {/* ── Overlay children (score bug, ticker, sponsor bug, etc.) ── */}
      {children}

      {/* ── Zoom preset strip (bottom-left) ── */}
      <div style={{
        position:    'absolute',
        bottom:      8,
        left:        8,
        zIndex:      30,
        display:     'flex',
        alignItems:  'center',
        gap:         3,
        ...glassSurface,
        borderRadius: 8,
        padding:     '3px 4px',
      }}>
        {ZOOM_PRESETS.map(level => {
          const active = zoom === level;
          return (
            <button
              key={level}
              onClick={() => setZoom(level)}
              style={{
                padding:          '4px 7px',
                borderRadius:     5,
                border:           'none',
                background:       active ? '#F97316' : 'transparent',
                color:            active ? '#fff' : 'rgba(255,255,255,0.65)',
                fontSize:         11,
                fontWeight:       active ? 800 : 600,
                cursor:           'pointer',
                touchAction:      'manipulation',
                userSelect:       'none',
                WebkitUserSelect: 'none' as any,
                transition:       'background 0.15s, color 0.15s',
                whiteSpace:       'nowrap',
                lineHeight:       1,
              }}
              aria-pressed={active}
              aria-label={`Zoom ${level}×`}
            >
              {level}×
            </button>
          );
        })}
      </div>

      {/* ── Custom fullscreen toggle (bottom-right) ── */}
      <button
        onClick={toggleFullscreen}
        style={fsBtnStyle}
        title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen
          ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
              <path d="M9 5V1M9 5h4M9 5L13 1M5 5V1M5 5H1M5 5L1 1M9 9v4M9 9h4M9 9l4 4M5 9v4M5 9H1M5 9l-4 4" />
            </svg>
          : <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
              <path d="M1 5V1h4M1 1l4 4M13 5V1H9M13 1l-4 4M1 9v4h4M1 13l4-4M13 9v4H9M13 13l-4-4" />
            </svg>
        }
      </button>

      {/* ── Loading indicator ── */}
      {loading && !error && !reconnecting && (
        <div style={{
          position:       'absolute',
          inset:          0,
          pointerEvents:  'none',
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'center',
          background:     'rgba(0,0,0,0.70)',
          color:          '#8899aa',
          gap:            12,
          zIndex:         5,
        }}>
          <div style={{ fontSize: 32 }}>📡</div>
          <div style={{ fontSize: 14 }}>Connecting to stream...</div>
        </div>
      )}

      {/* ── Auto-reconnect overlay (stream dropped, polling for recovery) ── */}
      {reconnecting && (
        <div style={{
          position:       'absolute',
          inset:          0,
          pointerEvents:  'none',
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'center',
          background:     'rgba(0,0,0,0.75)',
          color:          '#8899aa',
          gap:            12,
          zIndex:         5,
        }}>
          <div style={{ fontSize: 32 }}>📡</div>
          <div style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>Stream interrupted</div>
          <div style={{ fontSize: 12 }}>Reconnecting automatically…</div>
          <div style={{
            width: 28, height: 28,
            border: '3px solid rgba(255,255,255,0.15)',
            borderTopColor: '#F97316',
            borderRadius: '50%',
            animation: 'spin 0.9s linear infinite',
          }} />
        </div>
      )}

      {/* ── Error state ── */}
      {error && (
        <div style={{
          position:       'absolute',
          inset:          0,
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'center',
          background:     'rgba(0,0,0,0.85)',
          color:          '#8899aa',
          gap:            12,
          padding:        24,
          textAlign:      'center',
          zIndex:         5,
        }}>
          <div style={{ fontSize: 40 }}>📡</div>
          <div style={{ color: '#fff', fontWeight: 600 }}>Stream Unavailable</div>
          <div style={{ fontSize: 13 }}>{error}</div>
          <button
            className="btn btn-secondary"
            onClick={() => window.location.reload()}
            style={{ marginTop: 8 }}
          >
            Retry
          </button>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default VideoPlayer;
